import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { logger } from "@/lib/logger";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authed, object = authed
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // 401 is expected when not signed in; don't log noise for that case
      if (err?.response?.status !== 401) {
        logger.error("AuthContext.fetchMe failed:", err);
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  // Activity heartbeat — keeps `last_active_at` fresh so the Reports →
  // Activity "online now" panel and the header presence indicator are
  // accurate. Pings every 60s while the tab is visible. Also fires on focus
  // so returning from another tab/window immediately marks the user online.
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    let timer = null;
    const ping = async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      try { await api.post("/auth/heartbeat"); } catch { /* swallow */ }
    };
    ping();
    timer = setInterval(ping, 60_000);
    const onFocus = () => ping();
    const onVisibility = () => { if (!document.hidden) ping(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      if (timer) clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      logger.error("AuthContext.logout failed:", err);
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, refresh: fetchMe }),
    [user, loading, login, logout, fetchMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
